// Options Management System
// Centralized system for managing all user preferences and options

export interface AppOptions {
  // Display Units
  units: {
    depth: 'meters' | 'feet';
    temperature: 'celsius' | 'fahrenheit';
    pressure: 'bar' | 'psi';
    weight: 'kg' | 'lbs';
  };
  
  // Display Formats
  display: {
    timeFormat: '12h' | '24h';
    dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'DD MMM YYYY';
    theme: 'light' | 'dark' | 'auto' | 'aiDiveBotLight' | 'aiDiveBotDark' | 'aiDiveBotAuto';
    gasConsumptionMetric: 'rmv' | 'sac';
  };
  
  // Dive Equipment Settings
  equipment: {
    tankSize: 'AL80' | 'AL63' | 'AL100' | 'Steel72' | 'Steel100' | 'Steel120' | 'Steel130' | 'custom';
    customTankVolume?: number; // in liters
  };

  // Diver Profile Settings
  diver: {
    // Highest recreational/professional level — derived from recreationalCertifications
    // NAUI progression: a single Instructor level (legacy multi-level instructor
    // values are migrated to 'Instructor' on load)
    certification: 'none' | 'OW' | 'AOW' | 'Rescue' | 'DM' | 'Instructor';
    // Recreational/professional certifications — check all that you hold
    recreationalCertifications: {
      OW: boolean;
      AOW: boolean;
      Rescue: boolean;
      DM: boolean;
      Instructor: boolean;
    };
    // Photos of certification cards (compressed data URLs)
    certificationCardPhotos: Array<{
      id: string;
      dataUrl: string;
      uploadedAt: string;
    }>;
    // Specialty certifications — independent of the progression above
    specialtyCertifications: {
      wreck: boolean;
      advancedWreck: boolean;
      cavern: boolean;
      introCave: boolean;
      fullCave: boolean;
      tec40: boolean;
      tec45: boolean;
      tec50: boolean;
      tecTrimix: boolean;
    };
    gasCertifications: {
      nitrox: boolean;
      advancedNitrox: boolean;
      trimix: boolean;
      advancedTrimix: boolean;
    };
    // NAUI freediving/apnea progression — a separate track from the scuba
    // progression above, so a diver can hold any combination of the two
    freediveCertifications: {
      skinDiver: boolean;
      introFreediving: boolean;
      safeBuddy: boolean;
      freediver: boolean;
      intermediateFreediver: boolean;
      advancedFreediver: boolean;
      freediverSupervisor: boolean;
      freediverInstructor: boolean;
    };
    // NAUI freediving specialties — independent of the freedive progression
    freediveSpecialtyCertifications: {
      freediverSpearfishing: boolean;
      breathHoldSurvival: boolean;
    };
    // Dives completed before/outside this app, entered by hand so divers with a
    // long history don't have to import hundreds of logs for experience-aware advice
    previousDiveCount?: number;
  };
  
  // Activity Type Filters
  filters: {
    activityTypes: {
      Scubadiving: boolean;
      Freediving: boolean;
      Snorkeling: boolean;
      Spearfishing: boolean;
      Mermaiding: boolean;
    };
  };
  
  // Privacy & Data
  privacy: {
    // Whether other divers can find this account by name/email in buddy search
    discoverable: boolean;
    // Whether the email address is shown to other divers in search results and on
    // the public profile. Off by default — an email is the most sensitive field
    // we expose, and buddy requests work by name without it.
    showEmailToOthers: boolean;
    // What confirmed dive buddies can see about this diver
    shareDiveLogWithBuddies: boolean;
    shareCertificationsWithBuddies: boolean;
    shareEquipmentWithBuddies: boolean;
    // Whether Nereus may read the dive log and profile for personalized coaching.
    // On by default — it's the core of the product — but a diver can opt out.
    allowNereusPersonalization: boolean;
    // Whether Nereus sends a private reply when this diver @mentions him in a post or
    // comment. On by default; tracked separately from allowNereusPersonalization so a
    // diver can keep tailored /chat coaching while opting out of unsolicited social replies.
    allowNereusTagReplies: boolean;
    // Shows certifications on the public profile to everyone, not just accepted
    // buddies. shareCertificationsWithBuddies still gates the buddy-only view.
    showCertificationsPublicly: boolean;
    // Shows a map of dive site pins (location + dive count + max depth per site, no
    // per-dive dates) on the public profile to everyone. Off by default — unlike the
    // other public-profile toggles, this exposes real-world locations this diver visits.
    shareDiveMapPublicly: boolean;
    // How the dive count is shown on the public profile: the exact logged number,
    // or a broad range that doesn't reveal precisely how experienced this diver is.
    diveCountDisplay: 'exact' | 'range';
    // Which bucket to show when diveCountDisplay is "range" — one of
    // DIVE_COUNT_RANGE_LABELS, chosen by the diver instead of always being computed
    // from their real count. Null means "auto-compute from the real count".
    diveCountRangeOverride: string | null;
  };
}

// Default options
export const DEFAULT_OPTIONS: AppOptions = {
  // US units by default — the user base is primarily US
  units: {
    depth: 'feet',
    temperature: 'fahrenheit',
    pressure: 'psi',
    weight: 'lbs'
  },
  display: {
    timeFormat: '12h',
    dateFormat: 'MM/DD/YYYY',
    theme: 'aiDiveBotLight', // Default to Ocean light theme
    gasConsumptionMetric: 'rmv',
  },
  equipment: {
    tankSize: 'AL80', // Most common recreational tank
  },
  diver: {
    certification: 'none',
    recreationalCertifications: {
      OW: false,
      AOW: false,
      Rescue: false,
      DM: false,
      Instructor: false,
    },
    certificationCardPhotos: [],
    specialtyCertifications: {
      wreck: false,
      advancedWreck: false,
      cavern: false,
      introCave: false,
      fullCave: false,
      tec40: false,
      tec45: false,
      tec50: false,
      tecTrimix: false,
    },
    gasCertifications: {
      nitrox: false,
      advancedNitrox: false,
      trimix: false,
      advancedTrimix: false,
    },
    freediveCertifications: {
      skinDiver: false,
      introFreediving: false,
      safeBuddy: false,
      freediver: false,
      intermediateFreediver: false,
      advancedFreediver: false,
      freediverSupervisor: false,
      freediverInstructor: false,
    },
    freediveSpecialtyCertifications: {
      freediverSpearfishing: false,
      breathHoldSurvival: false,
    }
    // previousDiveCount is intentionally unset — absent means "not specified",
    // which reads differently to Nereus than an explicit 0
  },
  filters: {
    activityTypes: {
      Scubadiving: true,  // All activity types enabled by default
      Freediving: true,
      Snorkeling: true,
      Spearfishing: true,
      Mermaiding: true,
    }
  },
  privacy: {
    // Discoverable by default so buddy requests work out of the box, but the
    // email address stays hidden until the diver opts in to showing it
    discoverable: true,
    showEmailToOthers: false,
    shareDiveLogWithBuddies: true,
    shareCertificationsWithBuddies: true,
    shareEquipmentWithBuddies: false,
    allowNereusPersonalization: true,
    allowNereusTagReplies: true,
    showCertificationsPublicly: false,
    shareDiveMapPublicly: false,
    diveCountDisplay: 'exact',
    diveCountRangeOverride: null,
  }
};

// Local storage key
const OPTIONS_STORAGE_KEY = 'aidivecoach_options';

// Maps old combined certification values to the new specialtyCertifications flags
const LEGACY_SPECIALTY_MAP: Record<string, keyof AppOptions['diver']['specialtyCertifications']> = {
  'Wreck': 'wreck',
  'AdvancedWreck': 'advancedWreck',
  'Cavern': 'cavern',
  'IntroCave': 'introCave',
  'FullCave': 'fullCave',
  'Tec40': 'tec40',
  'Tec45': 'tec45',
  'Tec50': 'tec50',
  'TecTrimix': 'tecTrimix',
};

/**
 * Migrate a stored diver object that may contain the old combined certification enum.
 * If the stored certification is a legacy specialty value, set the matching flag in
 * specialtyCertifications and fall back the progression cert to 'none'.
 */
const migrateOldCertification = (diver: any): any => {
  const legacyKey = diver.certification as string;
  if (!(legacyKey in LEGACY_SPECIALTY_MAP)) {
    return diver;
  }
  const specialtyFlag = LEGACY_SPECIALTY_MAP[legacyKey];
  return {
    ...diver,
    certification: 'none',
    specialtyCertifications: {
      ...(diver.specialtyCertifications ?? {}),
      [specialtyFlag]: true,
    },
  };
};

// Recreational/professional progression order, lowest to highest
export const RECREATIONAL_CERT_ORDER: Array<keyof AppOptions['diver']['recreationalCertifications']> = [
  'OW', 'AOW', 'Rescue', 'DM', 'Instructor',
];

// Old multi-level instructor progression (PADI-style) collapsed into NAUI's single Instructor level
const LEGACY_INSTRUCTOR_LEVELS = ['OWSI', 'MSDT', 'IDC_Staff', 'MI', 'CD'];

export const normalizeLegacyCertification = (
  cert: string | undefined | null
): AppOptions['diver']['certification'] => {
  if (!cert || cert === 'none') return 'none';
  if (LEGACY_INSTRUCTOR_LEVELS.includes(cert)) return 'Instructor';
  return (RECREATIONAL_CERT_ORDER as string[]).includes(cert)
    ? (cert as AppOptions['diver']['certification'])
    : 'none';
};

export const normalizeRecreationalCertifications = (
  raw: unknown
): AppOptions['diver']['recreationalCertifications'] => {
  const map: AppOptions['diver']['recreationalCertifications'] = {
    OW: false, AOW: false, Rescue: false, DM: false, Instructor: false,
  };
  if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (value !== true) continue;
      if (key in map) {
        map[key as keyof typeof map] = true;
      } else if (LEGACY_INSTRUCTOR_LEVELS.includes(key)) {
        map.Instructor = true;
      }
    }
  }
  return map;
};

export const getAllRecreationalCertifications = (): Array<keyof AppOptions['diver']['recreationalCertifications']> => {
  return [...RECREATIONAL_CERT_ORDER];
};

export const hasAnyRecreationalCertification = (recCerts: AppOptions['diver']['recreationalCertifications']): boolean => {
  return Object.values(recCerts).some((v) => v === true);
};

/**
 * Derive the highest checked certification from the checkbox map.
 * Used to keep the legacy single `certification` value in sync for
 * consumers like flashcard deck gating and dive-site requirements.
 */
export const getHighestRecreationalCertification = (
  recCerts: AppOptions['diver']['recreationalCertifications']
): AppOptions['diver']['certification'] => {
  for (let i = RECREATIONAL_CERT_ORDER.length - 1; i >= 0; i--) {
    if (recCerts[RECREATIONAL_CERT_ORDER[i]]) {
      return RECREATIONAL_CERT_ORDER[i];
    }
  }
  return 'none';
};

/**
 * Expand a single highest-level certification into the checkbox map.
 * Progression is cumulative, so all levels up to and including it are checked.
 */
export const deriveRecreationalCertifications = (
  certification: AppOptions['diver']['certification']
): AppOptions['diver']['recreationalCertifications'] => {
  const map = {
    OW: false, AOW: false, Rescue: false, DM: false, Instructor: false,
  };
  const index = RECREATIONAL_CERT_ORDER.indexOf(certification as keyof AppOptions['diver']['recreationalCertifications']);
  for (let i = 0; i <= index; i++) {
    map[RECREATIONAL_CERT_ORDER[i]] = true;
  }
  return map;
};

/**
 * Migrate a stored diver object that predates the recreationalCertifications map.
 * Diving progression is cumulative, so check every level up to and including
 * the previously stored highest certification.
 */
const migrateSingleCertificationToMap = (diver: any): any => {
  if (diver.recreationalCertifications) {
    return diver;
  }
  return {
    ...diver,
    recreationalCertifications: deriveRecreationalCertifications(diver.certification ?? 'none'),
  };
};
/**
 * Keep only the boolean flags that exist in `defaults`, so a malformed or
 * outdated stored payload can't inject junk keys into a certification map.
 *
 * Declared above the class alongside the other loadFromStorage() helpers: the
 * OptionsManager singleton is constructed during this module's evaluation, so a
 * helper defined below the class would still be in its temporal dead zone and
 * would silently wipe every stored preference.
 */
export const normalizeBooleanFlagMap = <T extends Record<string, boolean>>(
  raw: unknown,
  defaults: T
): T => {
  const map = { ...defaults };
  if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (value === true && key in map) {
        map[key as keyof T] = true as T[keyof T];
      }
    }
  }
  return map;
};

// Broad, casual dive-count buckets — order matters, it's also the list of choices
// for diveCountRangeOverride, and must mirror DiveCountRangeLabels in
// UsersController.cs. Declared here (not further below with the rest of the dive
// count utilities) because normalizePrivacySettings, below, needs it during module
// evaluation — the same temporal-dead-zone reason as the other loadFromStorage()
// helpers; see normalizeBooleanFlagMap above.
export const DIVE_COUNT_RANGE_LABELS = ['0 dives', '1-10 dives', '11-25 dives', '26-50 dives', '51-100 dives', '100+ dives'] as const;

/**
 * Normalize stored privacy settings: keep only known keys, and only accept real
 * booleans (or, for diveCountDisplay/diveCountRangeOverride, one of their known
 * string values) so a corrupted or hand-edited value can't read as an opt-in.
 *
 * Declared above the class for the same temporal-dead-zone reason as the other
 * loadFromStorage() helpers — see normalizeBooleanFlagMap below.
 */
const normalizePrivacySettings = (raw: unknown): AppOptions['privacy'] => {
  const map = { ...DEFAULT_OPTIONS.privacy };
  if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (key === 'diveCountDisplay') {
        if (value === 'exact' || value === 'range') map.diveCountDisplay = value;
        continue;
      }
      if (key === 'diveCountRangeOverride') {
        map.diveCountRangeOverride = typeof value === 'string' && (DIVE_COUNT_RANGE_LABELS as readonly string[]).includes(value)
          ? value
          : null;
        continue;
      }
      if (typeof value === 'boolean' && key in map) {
        map[key as BooleanPrivacySettingKey] = value;
      }
    }
  }
  return map;
};

// Upper bound on the manually entered dive count — comfortably above a career
// total for even a lifelong instructor, low enough to reject typos and junk.
// Declared above the class: the OptionsManager singleton is constructed during
// this module's evaluation and reads this via loadFromStorage(), so anything
// defined below the class would still be in its temporal dead zone.
export const MAX_PREVIOUS_DIVE_COUNT = 50000;

/**
 * Normalize a manually entered previous-dive count from any source (text field,
 * localStorage, or the server's customSettings). Returns undefined for anything
 * that isn't a usable whole count, which reads as "not specified".
 */
export const normalizePreviousDiveCount = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' ? Number(value.trim()) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) return undefined;
  const whole = Math.floor(parsed);
  if (whole < 0 || whole > MAX_PREVIOUS_DIVE_COUNT) return undefined;
  return whole;
};

type LegacyThemePreference = 'newAchievementLight' | 'newAchievementDark';

const normalizeStoredTheme = (
  theme: AppOptions['display']['theme'] | LegacyThemePreference | undefined
): AppOptions['display']['theme'] => {
  if (theme === 'newAchievementLight') {
    return 'aiDiveBotLight';
  }

  if (theme === 'newAchievementDark') {
    return 'aiDiveBotDark';
  }

  return theme ?? DEFAULT_OPTIONS.display.theme;
};

/**
 * Options Manager - Handles all user preferences and options
 */
export class OptionsManager {
  private static instance: OptionsManager;
  private options: AppOptions;
  private listeners: Array<(options: AppOptions) => void> = [];

  private constructor() {
    this.options = this.loadFromStorage();
  }

  public static getInstance(): OptionsManager {
    if (!OptionsManager.instance) {
      OptionsManager.instance = new OptionsManager();
    }
    return OptionsManager.instance;
  }

  /**
   * Get current options
   */
  public getOptions(): AppOptions {
    return { ...this.options };
  }

  /**
   * Get a specific option value
   */
  public getOption<T extends keyof AppOptions>(category: T): AppOptions[T] {
    return this.options[category];
  }

  /**
   * Update options (deep merge)
   */
  public updateOptions(updates: Partial<AppOptions>): void {
    this.options = this.deepMerge(this.options, updates);
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Update a specific category of options
   */
  public updateCategory<T extends keyof AppOptions>(
    category: T, 
    updates: Partial<AppOptions[T]>
  ): void {
    this.options[category] = { ...this.options[category], ...updates };
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Reset to default options
   */
  public resetToDefaults(): void {
    this.options = { ...DEFAULT_OPTIONS };
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Reload options from localStorage
   * This is useful when localStorage has been updated externally
   */
  public reloadFromStorage(): void {
    this.options = this.loadFromStorage();
    this.notifyListeners();
  }

  /**
   * Subscribe to options changes
   */
  public subscribe(listener: (options: AppOptions) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Load options from localStorage
   */
  private loadFromStorage(): AppOptions {
    try {
      const stored = localStorage.getItem(OPTIONS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.display?.theme) {
          parsed.display.theme = normalizeStoredTheme(parsed.display.theme);
        }
        // Migrate legacy certification values that were split into specialtyCertifications
        if (parsed?.diver?.certification) {
          parsed.diver = migrateOldCertification(parsed.diver);
        }
        // Migrate the old single highest-level value into the checkbox map
        if (parsed?.diver) {
          parsed.diver = migrateSingleCertificationToMap(parsed.diver);
          // Collapse legacy multi-level instructor values into the single NAUI Instructor level
          parsed.diver.certification = normalizeLegacyCertification(parsed.diver.certification);
          parsed.diver.recreationalCertifications =
            normalizeRecreationalCertifications(parsed.diver.recreationalCertifications);
          parsed.diver.previousDiveCount =
            normalizePreviousDiveCount(parsed.diver.previousDiveCount);
          // Freedive maps are absent for profiles saved before the apnea track existed;
          // normalizing against the defaults fills them in and drops unknown flags
          parsed.diver.freediveCertifications = normalizeBooleanFlagMap(
            parsed.diver.freediveCertifications,
            DEFAULT_OPTIONS.diver.freediveCertifications
          );
          parsed.diver.freediveSpecialtyCertifications = normalizeBooleanFlagMap(
            parsed.diver.freediveSpecialtyCertifications,
            DEFAULT_OPTIONS.diver.freediveSpecialtyCertifications
          );
        }
        // Keep only known boolean privacy flags — a malformed value here would
        // otherwise become a truthy setting and quietly expose data
        if (parsed?.privacy) {
          parsed.privacy = normalizePrivacySettings(parsed.privacy);
        }
        // Merge with defaults to ensure all required properties exist
        return this.deepMerge(DEFAULT_OPTIONS, parsed);
      }
    } catch (error) {
      console.warn('Failed to load options from localStorage:', error);
    }

    return { ...DEFAULT_OPTIONS };
  }

  /**
   * Save options to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(this.options));
    } catch (error) {
      console.error('Failed to save options to localStorage:', error);
    }
  }

  /**
   * Notify all listeners of options changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getOptions());
      } catch (error) {
        console.error('Error in options listener:', error);
      }
    });
  }

  /**
   * Deep merge utility
   */
  private deepMerge<T>(target: T, source: Partial<T>): T {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] !== undefined) {
        if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
          result[key] = this.deepMerge(result[key], source[key]!);
        } else {
          result[key] = source[key]!;
        }
      }
    }
    
    return result;
  }
}

// Convenience hooks for React components
export const useOptions = () => {
  const optionsManager = OptionsManager.getInstance();
  return {
    options: optionsManager.getOptions(),
    updateOptions: (updates: Partial<AppOptions>) => optionsManager.updateOptions(updates),
    updateCategory: <T extends keyof AppOptions>(category: T, updates: Partial<AppOptions[T]>) => 
      optionsManager.updateCategory(category, updates),
    resetToDefaults: () => optionsManager.resetToDefaults()
  };
};

// Export the singleton instance for direct access
export const optionsManager = OptionsManager.getInstance();

// Tank size utility functions
export const getTankVolumeInLiters = (tankSize: AppOptions['equipment']['tankSize'], customVolume?: number): number => {
  const tankVolumes = {
    'AL80': 11.0,   // 80 cubic feet aluminum
    'AL63': 8.5,    // 63 cubic feet aluminum
    'AL100': 13.2,  // 100 cubic feet aluminum
    'Steel72': 9.6, // 72 cubic feet steel
    'Steel100': 12.0, // 100 cubic feet steel
    'Steel120': 15.1, // 120 cubic feet steel
    'Steel130': 16.4, // 130 cubic feet steel
    'custom': customVolume || 11.0, // fallback to AL80 if no custom volume
  };
  
  return tankVolumes[tankSize];
};

export const getTankDisplayName = (tankSize: AppOptions['equipment']['tankSize']): string => {
  const displayNames = {
    'AL80': 'AL80 (11.0L)',
    'AL63': 'AL63 (8.5L)',
    'AL100': 'AL100 (13.2L)',
    'Steel72': 'Steel 72 (9.6L)',
    'Steel100': 'Steel 100 (12.0L)',
    'Steel120': 'Steel 120 (15.1L)',
    'Steel130': 'Steel 130 (16.4L)',
    'custom': 'Custom Tank',
  };
  
  return displayNames[tankSize];
};

// NAUI Certification utility functions
export const getCertificationDisplayName = (certification: AppOptions['diver']['certification']): string => {
  const displayNames: Record<AppOptions['diver']['certification'], string> = {
    'none': 'Not Certified / Prefer Not to Say',
    'OW': 'Open Water Diver',
    'AOW': 'Advanced Open Water Diver',
    'Rescue': 'Rescue Diver',
    'DM': 'Divemaster',
    'Instructor': 'Instructor',
  };

  return displayNames[certification];
};

export const getCertificationDescription = (certification: AppOptions['diver']['certification']): string => {
  const descriptions: Record<AppOptions['diver']['certification'], string> = {
    'none': 'No certification selected or prefer not to specify',
    'OW': 'Entry-level certification (18m/60ft max depth)',
    'AOW': 'Advanced skills and deeper diving (30m/100ft max depth)',
    'Rescue': 'Emergency response and rescue skills',
    'DM': 'Professional level - assist instructors and guide dives',
    'Instructor': 'NAUI Instructor - teach and certify divers',
  };

  return descriptions[certification];
};

export const getCertificationLevel = (certification: AppOptions['diver']['certification']): number => {
  const levels: Record<AppOptions['diver']['certification'], number> = {
    'none': 0,
    'OW': 1,
    'AOW': 2,
    'Rescue': 3,
    'DM': 4,
    'Instructor': 5,
  };

  return levels[certification];
};

// Gas Mix Certification utility functions
export const getGasCertificationDisplayName = (gasType: keyof AppOptions['diver']['gasCertifications']): string => {
  const displayNames = {
    'nitrox': 'Enriched Air Nitrox (EANx)',
    'advancedNitrox': 'Advanced Nitrox',
    'trimix': 'Trimix',
    'advancedTrimix': 'Advanced Trimix',
  };
  
  return displayNames[gasType];
};

export const getGasCertificationDescription = (gasType: keyof AppOptions['diver']['gasCertifications']): string => {
  const descriptions = {
    'nitrox': 'Use enriched air (22-40% O2) for longer bottom times and shorter surface intervals',
    'advancedNitrox': 'Use higher oxygen percentages (40-100% O2) and decompression procedures',
    'trimix': 'Use helium-based gas mixes for deep diving (beyond 40m/130ft)',
    'advancedTrimix': 'Advanced trimix diving with complex gas switching and decompression',
  };
  
  return descriptions[gasType];
};

// Specialty Certification utility functions
export const getSpecialtyCertificationDisplayName = (cert: keyof AppOptions['diver']['specialtyCertifications']): string => {
  const displayNames: Record<keyof AppOptions['diver']['specialtyCertifications'], string> = {
    'wreck': 'Wreck Diver',
    'advancedWreck': 'Advanced Wreck Diver',
    'cavern': 'Cavern Diver',
    'introCave': 'Intro to Cave Diver',
    'fullCave': 'Full Cave Diver',
    'tec40': 'Tec 40',
    'tec45': 'Tec 45',
    'tec50': 'Tec 50',
    'tecTrimix': 'Tec Trimix',
  };

  return displayNames[cert];
};

export const getSpecialtyCertificationDescription = (cert: keyof AppOptions['diver']['specialtyCertifications']): string => {
  const descriptions: Record<keyof AppOptions['diver']['specialtyCertifications'], string> = {
    'wreck': 'Specialty training for non-technical wreck diving, line use, and hazard awareness',
    'advancedWreck': 'Advanced wreck procedures including penetration planning and overhead risk management',
    'cavern': 'Overhead-environment entry-level training within daylight-zone cavern limits',
    'introCave': 'Introductory cave diving with limited penetration and strict team procedures',
    'fullCave': 'Full cave diving — complex navigation, penetration, and cave emergency procedures',
    'tec40': 'Entry technical certification with limited decompression and staged cylinder procedures',
    'tec45': 'Extended technical training with accelerated decompression and deeper staged dives',
    'tec50': 'Advanced decompression to 50m/165ft with extended deco procedures',
    'tecTrimix': 'Helium-based technical diving for deep dives and narcosis management',
  };

  return descriptions[cert];
};

export const hasAnySpecialtyCertification = (specialtyCerts: AppOptions['diver']['specialtyCertifications']): boolean => {
  return Object.values(specialtyCerts).some((v) => v === true);
};

export const getAllSpecialtyCertifications = (): Array<keyof AppOptions['diver']['specialtyCertifications']> => {
  return ['wreck', 'advancedWreck', 'cavern', 'introCave', 'fullCave', 'tec40', 'tec45', 'tec50', 'tecTrimix'];
};

// NAUI Freediving (Apnea) Certification utility functions
// Names, depth limits, and static apnea targets follow the NAUI Standards and
// Policies Manual (2024 edition) apnea course standards.
type FreediveCert = keyof AppOptions['diver']['freediveCertifications'];
type FreediveSpecialtyCert = keyof AppOptions['diver']['freediveSpecialtyCertifications'];

// Freedive progression order, lowest to highest. Skin Diver, Intro to Freediving,
// and Safe Buddy sit below the certified Freediver levels; the pro credentials cap it.
export const FREEDIVE_CERT_ORDER: FreediveCert[] = [
  'skinDiver',
  'introFreediving',
  'safeBuddy',
  'freediver',
  'intermediateFreediver',
  'advancedFreediver',
  'freediverSupervisor',
  'freediverInstructor',
];

export const getFreediveCertificationDisplayName = (cert: FreediveCert): string => {
  const displayNames: Record<FreediveCert, string> = {
    'skinDiver': 'Skin Diver',
    'introFreediving': 'Introduction to Freediving',
    'safeBuddy': 'Safe Buddy',
    'freediver': 'Freediver',
    'intermediateFreediver': 'Intermediate Freediver',
    'advancedFreediver': 'Advanced Freediver',
    'freediverSupervisor': 'Freediver Supervisor',
    'freediverInstructor': 'Freediver Instructor',
  };

  return displayNames[cert];
};

export const getFreediveCertificationDescription = (cert: FreediveCert): string => {
  const descriptions: Record<FreediveCert, string> = {
    'skinDiver': 'Snorkeling and breath-hold diving certification — the usual prerequisite for Freediver',
    'introFreediving': 'Experience-only program (no certification) — static apnea to 2:00 or 25m dynamic',
    'safeBuddy': 'Safety and rescue techniques for uncertified freedivers — qualifies you for the Freediver course',
    'freediver': 'Entry-level freediver to 20m/66ft — static apnea 1:30, optional 25m dynamic',
    'intermediateFreediver': 'Freediving to 40m/130ft — static apnea 3:00, blackout handling and BLS recovery',
    'advancedFreediver': 'Freediving to 60m/197ft — static apnea 4:00, sink phase and advanced problem management',
    'freediverSupervisor': 'Professional level — organize and conduct dives for certified freedivers and assist instructors',
    'freediverInstructor': 'Teach and certify NAUI freedivers',
  };

  return descriptions[cert];
};

export const getFreediveCertificationMaxDepth = (cert: FreediveCert): string | null => {
  const maxDepths: Record<FreediveCert, string | null> = {
    'skinDiver': null,
    'introFreediving': null,
    'safeBuddy': null,
    'freediver': '20m/66ft',
    'intermediateFreediver': '40m/130ft',
    'advancedFreediver': '60m/197ft',
    'freediverSupervisor': null,
    'freediverInstructor': null,
  };

  return maxDepths[cert];
};

export const getAllFreediveCertifications = (): FreediveCert[] => {
  return [...FREEDIVE_CERT_ORDER];
};

export const hasAnyFreediveCertification = (
  freediveCerts: AppOptions['diver']['freediveCertifications']
): boolean => {
  return Object.values(freediveCerts).some((v) => v === true);
};

/**
 * Highest freedive level held, for depth-limit awareness and next-step suggestions.
 * Returns null when the diver holds no freediving certifications.
 */
export const getHighestFreediveCertification = (
  freediveCerts: AppOptions['diver']['freediveCertifications']
): FreediveCert | null => {
  for (let i = FREEDIVE_CERT_ORDER.length - 1; i >= 0; i--) {
    if (freediveCerts[FREEDIVE_CERT_ORDER[i]]) {
      return FREEDIVE_CERT_ORDER[i];
    }
  }
  return null;
};

export const getFreediveSpecialtyDisplayName = (cert: FreediveSpecialtyCert): string => {
  const displayNames: Record<FreediveSpecialtyCert, string> = {
    'freediverSpearfishing': 'Freediver Spearfishing & Collecting',
    'breathHoldSurvival': 'Breath Hold Survival',
  };

  return displayNames[cert];
};

export const getFreediveSpecialtyDescription = (cert: FreediveSpecialtyCert): string => {
  const descriptions: Record<FreediveSpecialtyCert, string> = {
    'freediverSpearfishing': 'Spearfishing, taking game, and collecting specimens while managing the added risk',
    'breathHoldSurvival': 'Breath-hold skills for surf, white water, and big-wave hold-downs — 3:00 static and high-metabolism apnea',
  };

  return descriptions[cert];
};

export const getAllFreediveSpecialtyCertifications = (): FreediveSpecialtyCert[] => {
  return ['freediverSpearfishing', 'breathHoldSurvival'];
};

export const hasAnyFreediveSpecialtyCertification = (
  certs: AppOptions['diver']['freediveSpecialtyCertifications']
): boolean => {
  return Object.values(certs).some((v) => v === true);
};

/**
 * Normalize a stored/synced freedive certification map, dropping unknown keys so a
 * malformed payload can't inject junk flags into the profile.
 */
export const normalizeFreediveCertifications = (
  raw: unknown
): AppOptions['diver']['freediveCertifications'] =>
  normalizeBooleanFlagMap(raw, DEFAULT_OPTIONS.diver.freediveCertifications);

export const normalizeFreediveSpecialtyCertifications = (
  raw: unknown
): AppOptions['diver']['freediveSpecialtyCertifications'] =>
  normalizeBooleanFlagMap(raw, DEFAULT_OPTIONS.diver.freediveSpecialtyCertifications);

export const getGasCertificationMaxDepth = (gasType: keyof AppOptions['diver']['gasCertifications']): string => {
  const maxDepths = {
    'nitrox': '40m/130ft (depending on O2%)',
    'advancedNitrox': '40m/130ft with deco',
    'trimix': '60m/200ft',
    'advancedTrimix': '100m+/330ft+',
  };
  
  return maxDepths[gasType];
};

export const hasAnyGasCertification = (gasCerts: AppOptions['diver']['gasCertifications']): boolean => {
  return Object.values(gasCerts).some(cert => cert === true);
};


// Time and Date formatting utility functions
export const getTimeFormatDisplayName = (timeFormat: AppOptions['display']['timeFormat']): string => {
  const displayNames = {
    '12h': '12-hour (3:45 PM)',
    '24h': '24-hour (15:45)',
  };
  
  return displayNames[timeFormat];
};

export const getDateFormatDisplayName = (dateFormat: AppOptions['display']['dateFormat']): string => {
  const displayNames = {
    'MM/DD/YYYY': 'US Format (01/15/2024)',
    'DD/MM/YYYY': 'European Format (15/01/2024)',
    'YYYY-MM-DD': 'ISO Format (2024-01-15)',
    'DD MMM YYYY': 'Text Format (15 Jan 2024)',
  };
  
  return displayNames[dateFormat];
};

export const formatTime = (date: Date | string, timeFormat: AppOptions['display']['timeFormat']): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (timeFormat === '12h') {
    return dateObj.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } else {
    return dateObj.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }
};

export const formatDate = (date: Date | string, dateFormat: AppOptions['display']['dateFormat']): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return dateObj.toLocaleDateString('en-US');
    case 'DD/MM/YYYY':
      return dateObj.toLocaleDateString('en-GB');
    case 'YYYY-MM-DD':
      return dateObj.toISOString().split('T')[0];
    case 'DD MMM YYYY':
      return dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    default:
      return dateObj.toLocaleDateString();
  }
};

export const formatDateTime = (
  date: Date | string,
  timeFormat: AppOptions['display']['timeFormat'],
  dateFormat: AppOptions['display']['dateFormat'],
  latitude?: number,
  longitude?: number
): string => {
  // If coordinates provided, use timezone-aware formatting
  if (latitude !== undefined && longitude !== undefined) {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const { formatDateTimeInLocation } = require('./timezoneUtils');
      const result = formatDateTimeInLocation(date, latitude, longitude, { time12h: timeFormat === '12h' });
      console.log(`Timezone formatting: raw=${date}, utc=${dateObj.toUTCString()}, lat=${latitude}, lon=${longitude}, timezone=${result.timezone}, formatted=${result.formatted}`);
      return result.formatted;
    } catch (error) {
      console.error('Error in timezone formatting, falling back to local time:', error);
    }
  }

  // Fallback to original local timezone formatting
  return `${formatDate(date, dateFormat)} ${formatTime(date, timeFormat)}`;
};

// Activity Type Filter utility functions
export const getActivityTypeDisplayName = (activityType: keyof AppOptions['filters']['activityTypes']): string => {
  const displayNames = {
    'Scubadiving': 'Scuba Diving',
    'Freediving': 'Freediving',
    'Snorkeling': 'Snorkeling',
    'Spearfishing': 'Spearfishing',
    'Mermaiding': 'Mermaiding',
  };
  
  return displayNames[activityType];
};

export const getActivityTypeDescription = (activityType: keyof AppOptions['filters']['activityTypes']): string => {
  const descriptions = {
    'Scubadiving': 'Traditional scuba diving with breathing apparatus',
    'Freediving': 'Breath-hold diving without breathing apparatus',
    'Snorkeling': 'Surface swimming with mask and snorkel',
    'Spearfishing': 'Underwater hunting and fishing activities',
    'Mermaiding': 'Recreational swimming with monofin and tail',
  };
  
  return descriptions[activityType];
};

export const getActivityTypeIcon = (activityType: keyof AppOptions['filters']['activityTypes']): string => {
  const icons = {
    'Scubadiving': '🤿',
    'Freediving': '🏊‍♂️',
    'Snorkeling': '🤽‍♂️',
    'Spearfishing': '🎣',
    'Mermaiding': '🧜‍♀️',
  };
  
  return icons[activityType];
};

export const getAllActivityTypes = (): Array<keyof AppOptions['filters']['activityTypes']> => {
  return ['Scubadiving', 'Freediving', 'Snorkeling', 'Spearfishing', 'Mermaiding'];
};

export const getEnabledActivityTypes = (filters: AppOptions['filters']['activityTypes']): Array<keyof AppOptions['filters']['activityTypes']> => {
  return getAllActivityTypes().filter(type => filters[type]);
};

// Filter function for dives based on activity type preferences
export const shouldIncludeDive = (diveType: string, activityTypeFilters: AppOptions['filters']['activityTypes']): boolean => {
  // Handle case where dive type might not exactly match our keys
  const normalizedDiveType = diveType as keyof AppOptions['filters']['activityTypes'];
  
  // If the dive type exists in our filters, use that preference
  if (normalizedDiveType in activityTypeFilters) {
    return activityTypeFilters[normalizedDiveType];
  }
  
  // For unknown dive types, default to showing them
  return true;
};

// Privacy utility functions
export type PrivacySettingKey = keyof AppOptions['privacy'];
// The boolean-valued privacy settings — rendered as simple toggles. diveCountDisplay
// is a string enum ('exact' | 'range') and gets its own dedicated control instead.
export type BooleanPrivacySettingKey = Exclude<PrivacySettingKey, 'diveCountDisplay' | 'diveCountRangeOverride'>;

export const getPrivacySettingDisplayName = (key: BooleanPrivacySettingKey): string => {
  const displayNames: Record<BooleanPrivacySettingKey, string> = {
    'discoverable': 'Discoverable in Buddy Search',
    'showEmailToOthers': 'Show My Email to Other Divers',
    'shareDiveLogWithBuddies': 'Share My Dive Log with Buddies',
    'shareCertificationsWithBuddies': 'Share My Certifications with Buddies',
    'shareEquipmentWithBuddies': 'Share My Equipment with Buddies',
    'allowNereusPersonalization': 'Let Nereus Use My Dive Data',
    'allowNereusTagReplies': 'Let Nereus Reply When I Tag Him',
    'showCertificationsPublicly': 'Show Certifications on My Public Profile',
    'shareDiveMapPublicly': 'Show My Dive Map on My Public Profile',
  };

  return displayNames[key];
};

export const getPrivacySettingDescription = (key: BooleanPrivacySettingKey): string => {
  const descriptions: Record<BooleanPrivacySettingKey, string> = {
    'discoverable': 'Let other divers find you by name to send a buddy request. Turn this off and only people you invite can add you.',
    'showEmailToOthers': 'Include your email address in search results and on your profile. Buddy requests work by name without it.',
    'shareDiveLogWithBuddies': 'Confirmed buddies can see the dives in your log',
    'shareCertificationsWithBuddies': 'Confirmed buddies can see the certifications you hold',
    'shareEquipmentWithBuddies': 'Confirmed buddies can see your gear list and service history',
    'allowNereusPersonalization': 'Nereus reads your dive log, certifications, and gear to personalize coaching — and, in your private chat with him, your groups, clubs, upcoming events, and recent group chat so he can catch you up. Turn this off and he answers generally, without your data.',
    'allowNereusTagReplies': "When you @mention Nereus in a post or comment, he sends you a private reply. Turn this off and tagging him does nothing.",
    'showCertificationsPublicly': 'Anyone viewing your profile can see your certifications, not just confirmed buddies.',
    'shareDiveMapPublicly': 'Anyone viewing your profile can see a map of the sites you\'ve dove, with dive count and max depth per site. Individual dive dates are never shown.',
  };

  return descriptions[key];
};

export const getAllPrivacySettings = (): BooleanPrivacySettingKey[] => {
  return [
    'discoverable',
    'showEmailToOthers',
    'shareDiveLogWithBuddies',
    'shareCertificationsWithBuddies',
    'shareEquipmentWithBuddies',
    'allowNereusPersonalization',
    'allowNereusTagReplies',
    'showCertificationsPublicly',
    'shareDiveMapPublicly',
  ];
};

export const DIVE_COUNT_DISPLAY_OPTIONS: Array<{ value: AppOptions['privacy']['diveCountDisplay']; label: string; description: string }> = [
  { value: 'exact', label: 'Exact number', description: 'Show the exact number of dives you’ve logged in the app' },
  { value: 'range', label: 'Broad range', description: 'Show a broad range instead of your exact dive count' },
];

// Broad, casual buckets similar to how dive counts are described in the diving
// community (e.g. certification card guidance) — chosen to obscure the exact count
// while still giving a rough sense of experience. DIVE_COUNT_RANGE_LABELS itself is
// declared earlier in this file, above normalizePrivacySettings — see the comment there.
export const getDiveCountRangeLabel = (diveCount: number): string => {
  if (diveCount <= 0) return DIVE_COUNT_RANGE_LABELS[0];
  if (diveCount <= 10) return DIVE_COUNT_RANGE_LABELS[1];
  if (diveCount <= 25) return DIVE_COUNT_RANGE_LABELS[2];
  if (diveCount <= 50) return DIVE_COUNT_RANGE_LABELS[3];
  if (diveCount <= 100) return DIVE_COUNT_RANGE_LABELS[4];
  return DIVE_COUNT_RANGE_LABELS[5];
};

// Theme utility functions
export const getThemeDisplayName = (theme: AppOptions['display']['theme']): string => {
  const displayNames = {
    'light': 'Magenta Light',
    'dark': 'Magenta Dark',
    'auto': 'Auto (System)',
    'aiDiveBotLight': 'Ocean Light',
    'aiDiveBotDark': 'Ocean Dark',
    'aiDiveBotAuto': 'Auto (System)',
  };

  return displayNames[theme];
};

export const getThemeDescription = (theme: AppOptions['display']['theme']): string => {
  const descriptions = {
    'light': 'Soft blush surfaces with bold magenta accents and gold highlights',
    'dark': 'Deep near-black surfaces with vivid magenta accents and gold highlights',
    'auto': 'Automatically follows your device\'s system preference (Magenta palette)',
    'aiDiveBotLight': 'Cool blue-grey surfaces with teal and green accents',
    'aiDiveBotDark': 'Deep ocean-blue surfaces with teal and green accents',
    'aiDiveBotAuto': 'Automatically follows your device\'s system preference (Ocean palette)',
  };

  return descriptions[theme];
};

export const getThemeIcon = (theme: AppOptions['display']['theme']): string => {
  const icons = {
    'light': '🌸',
    'dark': '💜',
    'auto': '🔄',
    'aiDiveBotLight': '🌊',
    'aiDiveBotDark': '🤿',
    'aiDiveBotAuto': '🔄',
  };

  return icons[theme];
};

// Determine the actual theme to use based on preference and system
export const getEffectiveTheme = (
  themePreference: AppOptions['display']['theme'] | LegacyThemePreference
): 'light' | 'dark' | 'aiDiveBotLight' | 'aiDiveBotDark' => {
  const prefersDark = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;

  if (themePreference === 'auto') {
    return prefersDark ? 'dark' : 'light';
  }

  if (themePreference === 'aiDiveBotAuto') {
    return prefersDark ? 'aiDiveBotDark' : 'aiDiveBotLight';
  }

  const normalizedTheme = normalizeStoredTheme(themePreference);

  if (normalizedTheme === 'auto') return 'light';
  if (normalizedTheme === 'aiDiveBotAuto') return prefersDark ? 'aiDiveBotDark' : 'aiDiveBotLight';
  return normalizedTheme;
};